<template>
	<div class="crRoot">
		<nav class="tabs" aria-label="Code review pages">
			<router-link :to="{ name: 'codeReviewQueue' }">Findings</router-link>
			<router-link :to="{ name: 'codeReviewPushes' }">Reviewed pushes</router-link>
			<router-link :to="{ name: 'codeReviewFixes' }">Fixes</router-link>
			<router-link :to="{ name: 'codeReviewReport' }">Report</router-link>
		</nav>

		<div v-if="error" class="errorBox">{{error}}</div>
		<div v-if="loading && !data" class="loadingInline"><ScaleLoader /> Loading…</div>

		<template v-if="data">
			<!-- Heading -->
			<div class="headRow">
				<h1 class="pageHeading"><span class="mono">{{f.displayId}}</span> {{f.title}}</h1>
			</div>
			<div class="row">
				<span class="badge" :class="severityClass(f.effectiveSeverity)">{{f.effectiveSeverity}}</span>
				<span v-if="f.category === 'Security'" class="badge securityBadge">Security</span>
				<span class="badge" :class="statusClass(f.status)">{{label(f.status)}}</span>
				<span v-if="f.resolution && f.resolution !== 'None'" class="hintText">Resolution: {{label(f.resolution)}}<template v-if="f.rejectReason && f.rejectReason !== 'None'"> ({{label(f.rejectReason)}})</template><template v-if="f.resolvedBy"> by {{f.resolvedBy}}, {{formatTime(f.resolvedAt)}}</template></span>
				<span class="hintText">{{f.repo}} · <a v-if="f.fileUrl" :href="f.fileUrl" target="_blank" rel="noopener" class="mono">{{location(f)}}</a><span v-else class="mono">{{location(f)}}</span></span>
				<span class="hintText">Created {{formatTime(f.createdAt)}}</span>
				<span v-if="loading" class="loadingInline"><ScaleLoader /></span>
			</div>
			<div v-if="f.rejectNote && f.rejectCommentId" class="hintText">Rejected with a comment: <button type="button" class="linkButton" @click="showComment(f.rejectCommentId)">read it in Comments</button></div>
			<div v-else-if="f.rejectNote" class="hintText">Rejection note: <span class="preText">{{f.rejectNote}}</span></div>

			<!-- Actions -->
			<div class="card actions">
				<div class="row">
					<template v-if="data.canWrite">
						<button type="button" v-if="!f.isResolved" class="crAction danger" @click="openReject" :disabled="busy" title="Close this finding without a fix: you choose a reason (invalid, intentional, not worth fixing, duplicate…) and can add a note.">Reject…</button>
						<button type="button" v-if="!f.isResolved" class="crAction positive" @click="markFixed" :disabled="busy" title="Close this finding because the code no longer has the problem (fixed by a commit that did not mention its CR id, or by other work).">Mark already fixed</button>
						<button type="button" v-if="f.status !== 'Open'" class="crAction" @click="reopen" :disabled="busy" title="Set this finding back to Open.">Reopen</button>
					</template>
					<button type="button" class="crAction" @click="copyForAi" title="Copy this finding for pasting into an AI chat: its text, severity, triage, push and comments, between clearly marked begin and end lines.">Copy for AI chat</button>
					<button type="button" class="crAction assist" @click="generatePrompt" :disabled="busy" title="Write a Markdown prompt for fixing this finding with a coding agent of your choice (e.g. Claude Code in your own checkout). The prompt tells the agent to pull the latest code and check that the finding still applies first. Nothing runs here.">Generate prompt</button>
					<span v-if="busy" class="loadingInline"><ScaleLoader /> Working…</span>
					<span v-if="!data.canWrite" class="hintText">You can view this finding; acting on it needs the Code Review Write permission.</span>
				</div>
				<div v-if="showFixRow" class="row fixRow" role="group" aria-label="Autonomous fix">
					<span class="fixLabel">Autonomous fix:</span>
					<template v-if="data.canApproveFixes && data.fixesEnabled && f.status === 'Open'">
						<button type="button" class="crAction positive" @click="approveFix(false)" :disabled="busy" title="Queue this finding for the next fix batch: the agent fixes the queued findings together on a separate branch, and nothing is pushed until someone approves the push.">Approve for batch fix</button>
						<slide-button :key="'slide_immediate_' + slideCounter"
									  class="crSlide"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="260"
									  :height="28"
									  :color="slideColors.caution"
									  text="slide to fix immediately"
									  success-text="approving"
									  :disabled="busy"
									  title="Start a fix batch for this finding alone, as soon as the agent is free. The agent edits and commits on a separate branch; nothing is pushed until someone approves the push."
									  @completed="approveFix(true)" />
					</template>
					<button type="button" v-if="data.canApproveFixes && data.canUnapproveFix" class="crAction" @click="unapproveFix" :disabled="busy" title="Take this finding back out of the fix queue (it becomes Open again).">Unapprove</button>
					<span v-if="data.fixBatch" class="hintText">
						{{f.status === 'FixInProgress' ? 'In' : 'Latest'}} fix batch
						<router-link :to="{ name: 'codeReviewFixBatch', params: { id: data.fixBatch.id } }">{{data.fixBatch.displayId}}</router-link>
						<span class="badge" :class="statusClass(data.fixBatch.status)">{{label(data.fixBatch.status)}}</span>
					</span>
					<span v-if="f.status === 'ApprovedBatch'" class="hintText">Waits for the next fix batch (<router-link :to="{ name: 'codeReviewFixes' }">Fixes</router-link>).</span>
					<span v-if="!data.fixesEnabled" class="hintText">Autonomous fixes are turned off (setting codeReviewFixesEnabled).</span>
					<span v-else-if="!data.canApproveFixes && f.status === 'Open'" class="hintText">Approving fixes needs the Code Review Approve Fixes permission.</span>
				</div>
				<div v-if="actionError" class="errorBox" role="alert">{{actionError}}</div>
				<div v-if="actionInfo" class="hintText" role="status">{{actionInfo}}<router-link v-if="infoBatchId" :to="{ name: 'codeReviewFixBatch', params: { id: infoBatchId } }"> Open FB-{{infoBatchId}}</router-link></div>
			</div>

			<!-- AI re-evaluation -->
			<section class="card" aria-labelledby="crfReEvalHeading">
				<div class="row sectionHead">
					<h2 id="crfReEvalHeading" class="secondaryHeading">AI re-evaluation</h2>
					<slide-button v-if="data.canWrite"
								  :key="'slide_reEvaluate_' + slideCounter"
								  class="crSlide"
								  :auto-width="false"
								  :circle="true"
								  :noanimate="true"
								  :width="220"
								  :height="28"
								  :color="slideColors.assist"
								  text="slide to re-evaluate"
								  success-text="queuing"
								  :disabled="busy"
								  title="Queue an AI re-evaluation of this finding at the default branch's head (a Claude job on the agent)."
								  @completed="reEvaluate" />
				</div>
				<div v-if="suggestion" class="suggestion" role="group" aria-labelledby="crfSuggestionHeading">
					<h3 id="crfSuggestionHeading" class="subHeading">Suggestion awaiting a decision</h3>
					<div class="row">
						<span>
							At <a v-if="suggestion.checkedShaUrl" :href="suggestion.checkedShaUrl" target="_blank" rel="noopener" class="mono">{{shortSha(suggestion.checkedSha)}}</a><span v-else class="mono">{{shortSha(suggestion.checkedSha)}}</span>
							the finding is <b>{{suggestion.statusLabel}}</b> ({{suggestion.confidence}} confidence).
						</span>
					</div>
					<div class="row">
						<span>Suggested action: <b>{{suggestion.actionLabel}}</b><template v-if="suggestion.rejectReason"> ({{label(suggestion.rejectReason)}})</template></span>
						<span v-if="suggestion.suggestedSeverity">
							Severity: <span class="badge" :class="severityClass(f.effectiveSeverity)">{{f.effectiveSeverity}}</span>
							→ <span class="badge" :class="severityClass(suggestion.suggestedSeverity)">{{suggestion.suggestedSeverity}}</span>
						</span>
					</div>
					<div class="hintText">
						From run #{{suggestion.runId}}, {{formatTime(suggestion.at)}}.
						<button type="button" v-if="suggestion.commentId" class="linkButton" @click="showComment(suggestion.commentId)">Read the agent's explanation and evidence</button>
					</div>
					<div v-if="data.canWrite" class="row suggestionButtons">
						<slide-button v-if="suggestion.action === 'approve_immediate_fix'"
									  :key="'slide_accept_' + slideCounter"
									  class="crSlide"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="260"
									  :height="28"
									  :color="slideColors.positive"
									  text="slide to accept and fix now"
									  success-text="accepting"
									  :disabled="busy || !suggestion.canAccept"
									  title="Apply the suggested action: start a fix batch for this finding as soon as the agent is free."
									  @completed="acceptSuggestion" />
						<button type="button" v-else class="crAction positive" @click="acceptSuggestion" :disabled="busy || !suggestion.canAccept" :aria-describedby="suggestion.acceptBlockedReason ? 'crfAcceptBlocked' : null" title="Apply the suggested action to this finding.">Accept</button>
						<label for="crfDeclineNote" class="visuallyHidden">Comment on declining (optional; added to the comments)</label>
						<input id="crfDeclineNote" type="text" v-model="declineNote" placeholder="comment on declining (optional)" title="Added to the finding's comments." maxlength="2000" :disabled="busy" />
						<button type="button" class="crAction danger" @click="declineSuggestion" :disabled="busy" title="Dismiss the suggestion and leave the finding as it is.">Decline</button>
						<span v-if="suggestion.acceptBlockedReason" id="crfAcceptBlocked" class="hintText">{{suggestion.acceptBlockedReason}}</span>
					</div>
					<div v-else class="hintText">Accepting or declining needs the Code Review Write permission.</div>
				</div>
				<template v-if="re">
					<div class="row">
						<span>Latest request:</span>
						<span class="badge" :class="statusClass(re.status)">{{reStatusLabel(re)}}</span>
						<span class="hintText">
							{{triggerLabel(re.trigger)}}<template v-if="re.triggerTargetId"> ({{re.triggerTargetId}})</template>, requested by {{re.requestedBy}} {{formatTime(re.requestedAt)}}
							<template v-if="re.checkedSha">· checked <a v-if="re.checkedShaUrl" :href="re.checkedShaUrl" target="_blank" rel="noopener" class="mono">{{shortSha(re.checkedSha)}}</a><span v-else class="mono">{{shortSha(re.checkedSha)}}</span></template>
							<template v-else>· {{re.requestedSha ? 'at ' + shortSha(re.requestedSha) : 'at the default branch head'}}</template>
						</span>
						<span v-if="re.run" class="hintText">· run #{{re.run.id}} <span class="badge" :class="statusClass(re.run.status)">{{label(re.run.status)}}</span><span v-if="re.run.costUsd !== null && re.run.costUsd !== undefined" class="runCost">{{formatCost(re.run.costUsd)}}</span></span>
						<span v-if="re.isActive" class="loadingInline" aria-hidden="true"><ScaleLoader /></span>
					</div>
					<div v-if="re.statusReason" class="hintText preText">{{re.statusReason}}</div>
					<div v-if="!state.reviewsEnabled && re.isActive" class="warningBox">Code review is turned off (setting codeReviewEnabled), so this request waits until it is turned on.</div>
					<details v-if="data.isAdmin && re.run" class="rawOutput" @toggle="rawToggled($event)">
						<summary>Raw output of run #{{re.run.id}} (admin)</summary>
						<div v-if="raw.loading" class="loadingInline"><ScaleLoader /> Loading…</div>
						<div v-else-if="raw.error" class="errorBox">{{raw.error}}</div>
						<template v-else-if="raw.data">
							<div v-if="!raw.data.found" class="hintText">No output stored for this run.</div>
							<template v-else>
								<div class="subHeading">Result JSON</div>
								<pre class="scrollPre">{{prettyJson(raw.data.resultJson)}}</pre>
								<div class="subHeading">Log tail</div>
								<pre class="scrollPre">{{raw.data.logTail || '(empty)'}}</pre>
							</template>
						</template>
					</details>
				</template>
				<div v-else-if="!suggestion" class="hintText">Not re-evaluated yet. Re-evaluate asks the AI agent to check this finding against the current code of the default branch and suggest an action. Nothing changes until someone accepts the suggestion, except that a confident rejection as intended or not worth fixing closes a low or medium, non-security finding by itself.</div>
			</section>

			<!-- Title and description -->
			<section class="card" aria-labelledby="crfTextHeading">
				<div class="row sectionHead">
					<h2 id="crfTextHeading" class="secondaryHeading">Description</h2>
					<button type="button" v-if="data.canWrite && !editing" @click="startEdit" :disabled="busy">Edit</button>
					<span v-if="f.version > 1" class="hintText">edited, version {{f.version}}</span>
					<button type="button" v-if="data.textVersions.length" class="linkButton" @click="showTextVersions = !showTextVersions" :aria-expanded="showTextVersions ? 'true' : 'false'">{{showTextVersions ? 'Hide' : 'View'}} previous versions ({{data.textVersions.length}})</button>
				</div>
				<template v-if="editing">
					<div class="editGrid">
						<label for="crfEditTitle">Title</label>
						<input id="crfEditTitle" ref="editTitle" type="text" v-model="editTitle" maxlength="300" />
						<label for="crfEditDescription">Description</label>
						<textarea id="crfEditDescription" v-model="editDescription" rows="8"></textarea>
					</div>
					<div class="row">
						<button type="button" @click="saveEdit" :disabled="busy || !editTitle.trim()">Save</button>
						<button type="button" @click="cancelEdit" :disabled="busy">Cancel</button>
					</div>
				</template>
				<div v-else class="preText">{{f.description || '(no description)'}}</div>
				<div v-if="showTextVersions" class="versions">
					<div v-for="(v, i) in data.textVersions" :key="i" class="version">
						<div class="hintText">Version {{i + 1}}, replaced {{formatTime(v.replacedAt)}} by {{v.replacedBy}}</div>
						<div><b>{{v.title}}</b></div>
						<div class="preText">{{v.text}}</div>
					</div>
				</div>
			</section>

			<div class="twoCol">
				<!-- Severity and triage -->
				<section class="card" aria-labelledby="crfTriageHeading">
					<h2 id="crfTriageHeading" class="secondaryHeading">Severity and triage</h2>
					<table class="infoTable">
						<tbody>
							<tr>
								<th scope="row">Severity</th>
								<td>
									claimed <span class="badge" :class="severityClass(f.claimedSeverity)">{{f.claimedSeverity}}</span>
									→ final <span class="badge" :class="severityClass(f.finalSeverity)">{{f.finalSeverity}}</span>
									→ effective <span class="badge" :class="severityClass(f.effectiveSeverity)">{{f.effectiveSeverity}}</span>
								</td>
							</tr>
							<tr><th scope="row">Rules applied</th><td>{{f.rulesApplied.length ? f.rulesApplied.join(', ') : 'none'}}</td></tr>
							<tr><th scope="row">Verdict</th><td>{{f.verdict}}</td></tr>
							<tr><th scope="row">Category</th><td><span v-if="f.category === 'Security'" class="badge securityBadge">Security</span><template v-else>{{label(f.category)}}</template></td></tr>
							<tr><th scope="row">Introduced by push</th><td>{{f.introducedByPush}}</td></tr>
							<tr><th scope="row">Fix complexity</th><td>{{label(f.fixComplexity)}}</td></tr>
							<tr><th scope="row">Notification</th><td>{{f.notify ? 'notifies' : 'queue only'}}<template v-if="f.notifiedAt">, sent {{formatTime(f.notifiedAt)}}</template></td></tr>
							<tr v-if="f.duplicateOfId"><th scope="row">Duplicate of</th><td><router-link :to="findingRoute(f.duplicateOfId)">CR-{{f.duplicateOfId}}</router-link></td></tr>
						</tbody>
					</table>
					<form v-if="data.canWrite" class="row severityForm" @submit.prevent="changeSeverity">
						<label for="crfSeverity">Change severity</label>
						<select id="crfSeverity" v-model="severityChoice" :disabled="busy">
							<option v-for="s in severities" :key="s" :value="s">{{s}}</option>
						</select>
						<label for="crfSeverityNote" class="visuallyHidden">Reason for the severity change</label>
						<input id="crfSeverityNote" type="text" v-model="severityNote" placeholder="reason (optional)" maxlength="2000" :disabled="busy" />
						<button type="submit" :disabled="busy || severityChoice === f.effectiveSeverity">Apply</button>
					</form>
					<h3 class="subHeading">Triage rationale</h3>
					<div class="preText">{{f.triageRationale || '(none)'}}</div>
					<template v-if="f.demotionEvidence.length">
						<h3 class="subHeading">Demotion evidence</h3>
						<ul class="compactList">
							<li v-for="(e, i) in f.demotionEvidence" :key="i">
								<a v-if="e.url" :href="e.url" target="_blank" rel="noopener" class="mono">{{e.file}}{{e.line ? ':' + e.line : ''}}</a><span v-else class="mono">{{e.file}}{{e.line ? ':' + e.line : ''}}</span><span v-if="e.explanation">: {{e.explanation}}</span>
							</li>
						</ul>
					</template>
					<template v-if="f.intentEvidence && f.intentEvidence.length">
						<h3 class="subHeading">Recorded as intended</h3>
						<ul class="compactList">
							<li v-for="(e, i) in f.intentEvidence" :key="i"><span class="mono">{{e.source}}</span>: {{e.explanation}}</li>
						</ul>
					</template>
				</section>

				<!-- Original reviewer text -->
				<section class="card" aria-labelledby="crfOrigHeading">
					<h2 id="crfOrigHeading" class="secondaryHeading">Original reviewer report</h2>
					<h3 class="subHeading">Summary</h3>
					<div class="preText">{{f.originalSummary || '(none)'}}</div>
					<h3 class="subHeading">Failure scenario</h3>
					<div class="preText">{{f.originalScenario || '(none)'}}</div>
				</section>
			</div>

			<!-- Push -->
			<section class="card" aria-labelledby="crfPushHeading">
				<h2 id="crfPushHeading" class="secondaryHeading">Reviewed push</h2>
				<div v-if="!t" class="hintText">The push {{f.targetId}} is no longer stored.</div>
				<template v-else>
					<table class="infoTable">
						<tbody>
							<tr><th scope="row">Push</th><td><router-link :to="{ name: 'codeReviewPush', params: { id: t.id } }">{{t.repo}} push {{t.pushId}}</router-link> <span class="badge" :class="statusClass(t.status)">{{label(t.status)}}</span> <span class="hintText">{{t.statusReason}}</span></td></tr>
							<tr><th scope="row">Pushed</th><td>{{formatTime(t.pushedAt)}} by {{t.pusher || 'unknown'}}</td></tr>
							<tr><th scope="row">Range</th><td class="mono">{{shortSha(t.oldSha)}}..<a v-if="t.newShaUrl" :href="t.newShaUrl" target="_blank" rel="noopener">{{shortSha(t.newSha)}}</a><template v-else>{{shortSha(t.newSha)}}</template></td></tr>
						</tbody>
					</table>
					<ul class="compactList">
						<li v-for="c in t.commits" :key="c.sha"><a v-if="c.url" :href="c.url" target="_blank" rel="noopener" class="mono">{{shortSha(c.sha)}}</a><span v-else class="mono">{{shortSha(c.sha)}}</span> {{c.subject}} <span v-if="c.author" class="hintText">({{c.author}})</span></li>
					</ul>
				</template>
				<template v-if="data.fixCommits.length">
					<h3 class="subHeading">Linked fix commits</h3>
					<ul class="compactList">
						<li v-for="c in data.fixCommits" :key="c.text"><a v-if="c.url" :href="c.url" target="_blank" rel="noopener" class="mono">{{shortSha(c.text)}}</a><span v-else class="mono">{{c.text}}</span></li>
					</ul>
				</template>
				<template v-if="data.duplicateGroup.length">
					<h3 class="subHeading">Same duplicate group</h3>
					<ul class="compactList">
						<li v-for="d in data.duplicateGroup" :key="d.id"><router-link :to="findingRoute(d.id)">{{d.displayId}}</router-link> <span class="badge" :class="severityClass(d.effectiveSeverity)">{{d.effectiveSeverity}}</span> <span class="badge" :class="statusClass(d.status)">{{label(d.status)}}</span> {{d.title}}</li>
					</ul>
				</template>
				<template v-if="data.repeats.length">
					<h3 class="subHeading">Raised again by later pushes</h3>
					<ul class="compactList">
						<li v-for="(r, i) in data.repeats" :key="i">{{formatTime(r.at)}}: <router-link v-if="r.targetId" :to="{ name: 'codeReviewPush', params: { id: r.targetId } }">{{r.targetId}}</router-link> <span class="hintText">({{r.actor}})</span></li>
					</ul>
				</template>
			</section>

			<!-- Comments -->
			<section class="card" aria-labelledby="crfCommentsHeading">
				<h2 id="crfCommentsHeading" class="secondaryHeading">Comments ({{data.comments.length}})</h2>
				<div v-for="c in data.comments" :key="c.id" :id="'crfC' + c.id" class="comment" tabindex="-1">
					<div class="row commentHead">
						<b>{{c.author}}</b>
						<span class="hintText">{{formatTime(c.createdAt)}}</span>
						<span v-if="c.editedAt" class="hintText">edited {{formatTime(c.editedAt)}}<template v-if="c.editedBy && c.editedBy !== c.author"> by {{c.editedBy}}</template>, version {{c.version}}</span>
						<button type="button" v-if="data.canWrite && editingCommentId !== c.id" class="linkButton" @click="startCommentEdit(c)" :disabled="busy">Edit</button>
						<button type="button" v-if="c.previousVersions.length" class="linkButton" @click="toggleCommentVersions(c.id)" :aria-expanded="openCommentVersions[c.id] ? 'true' : 'false'">{{openCommentVersions[c.id] ? 'Hide' : 'View'}} previous versions ({{c.previousVersions.length}})</button>
					</div>
					<template v-if="editingCommentId === c.id">
						<label :for="'crfComment' + c.id" class="visuallyHidden">Edit comment</label>
						<textarea :id="'crfComment' + c.id" v-model="editingCommentText" rows="4" class="wide"></textarea>
						<div class="row">
							<button type="button" @click="saveCommentEdit(c)" :disabled="busy || !editingCommentText.trim()">Save</button>
							<button type="button" @click="editingCommentId = null" :disabled="busy">Cancel</button>
						</div>
					</template>
					<div v-else class="preText">{{c.text}}</div>
					<div v-if="openCommentVersions[c.id]" class="versions">
						<div v-for="(v, i) in c.previousVersions" :key="i" class="version">
							<div class="hintText">Version {{i + 1}}, replaced {{formatTime(v.replacedAt)}} by {{v.replacedBy}}</div>
							<div class="preText">{{v.text}}</div>
						</div>
					</div>
				</div>
				<div v-if="!data.comments.length" class="hintText">No comments yet.</div>
				<form v-if="data.canWrite" class="addComment" @submit.prevent="addComment">
					<label for="crfNewComment">Add a comment</label>
					<textarea id="crfNewComment" v-model="newComment" rows="3" class="wide"></textarea>
					<div class="row"><button type="submit" :disabled="busy || !newComment.trim()">Add comment</button></div>
				</form>
			</section>

			<!-- History -->
			<section class="card" aria-labelledby="crfHistoryHeading">
				<h2 id="crfHistoryHeading" class="secondaryHeading">History</h2>
				<table class="dense history">
					<thead>
						<tr><th scope="col">When</th><th scope="col">Who</th><th scope="col">What</th><th scope="col">Details</th></tr>
					</thead>
					<tbody>
						<tr v-for="e in data.events" :key="e.id">
							<td class="nowrap">{{formatTime(e.at)}}</td>
							<td>{{e.actor}}</td>
							<td class="nowrap">{{label(e.type)}}</td>
							<td class="preText">{{eventDetails(e)}}</td>
						</tr>
					</tbody>
				</table>
			</section>
		</template>

		<!-- Reject dialog -->
		<div v-if="rejectOpen" class="overlay" @keydown.esc="closeReject">
			<div class="dialog" role="dialog" aria-modal="true" aria-labelledby="crfRejectTitle">
				<h2 id="crfRejectTitle" class="secondaryHeading">Reject {{f.displayId}}</h2>
				<div id="crfRejectReasonLabel" class="dialogLabel">Reason</div>
				<div class="reasonButtons" role="group" aria-labelledby="crfRejectReasonLabel">
					<button v-for="(r, i) in rejectReasons" :key="r.value" type="button" :ref="i === 0 ? 'firstReason' : null" class="reasonButton" :class="{ selected: rejectReason === r.value }" :aria-pressed="rejectReason === r.value ? 'true' : 'false'" @click="rejectReason = r.value">{{r.label}}</button>
				</div>
				<label for="crfRejectNote" class="dialogLabel">Comment (optional)</label>
				<textarea id="crfRejectNote" v-model="rejectNote" rows="4" class="wide resizable" maxlength="2000" aria-describedby="crfRejectNoteHint"></textarea>
				<div id="crfRejectNoteHint" class="hintText">Added to the finding's comments, where a re-evaluation reads it.</div>
				<div v-if="actionError" class="errorBox" role="alert">{{actionError}}</div>
				<div class="row dialogButtons">
					<button type="button" class="crAction danger" @click="confirmReject" :disabled="busy || !rejectReason">Reject</button>
					<button type="button" @click="closeReject" :disabled="busy">Cancel</button>
				</div>
			</div>
		</div>

		<!-- Prompt dialog -->
		<div v-if="promptOpen" class="overlay" @keydown.esc="promptOpen = false">
			<div class="dialog wideDialog" role="dialog" aria-modal="true" aria-labelledby="crfPromptTitle">
				<h2 id="crfPromptTitle" class="secondaryHeading">Fix prompt for {{f.displayId}}</h2>
				<div class="hintText">Give this to a coding agent (for example Claude Code) in a checkout of {{f.repo}}. It tells the agent to check the issue still exists first, and to commit with "Fixes {{f.displayId}}".</div>
				<div v-if="prompt.headError" class="warningBox">The current {{prompt.headBranch || 'default branch'}} head could not be looked up: {{prompt.headError}}</div>
				<label for="crfPromptText" class="visuallyHidden">Prompt markdown</label>
				<textarea id="crfPromptText" ref="promptText" class="wide promptText" readonly rows="20" :value="prompt.markdown"></textarea>
				<div class="row dialogButtons">
					<button type="button" ref="copyButton" @click="copyPrompt">Copy</button>
					<button type="button" @click="downloadPrompt">Download {{prompt.fileName}}</button>
					<button type="button" @click="promptOpen = false">Close</button>
					<span class="hintText" role="status">{{copyStatus}}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { GetFinding, EditFinding, AddComment, EditComment, SetStatus, SetSeverity, GeneratePrompt, ReEvaluate, GetReEvaluationState, AcceptSuggestion, DeclineSuggestion, GetRunOutput, ApproveFix, UnapproveFix } from 'appRoot/api/CodeReviewData';
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { severities, rejectReasons, label, severityClass, statusClass, formatTime, formatCost, location, shortSha, slideColors } from './codeReviewFormat';

	/** How often the page checks a queued or running re-evaluation. */
	const reEvaluationPollMs = 10000;

	function statusText(s)
	{
		return s ? s.replace(/\/None$/, '').replace('/', ', ') : '';
	}

	/** The suggestion JSON stored in ActionSuggested / ActionAccepted / ActionDeclined events, or null. */
	function parseSuggestion(json)
	{
		if (!json)
			return null;
		try
		{
			return JSON.parse(json);
		}
		catch (ex)
		{
			return null;
		}
	}

	function describeSuggestion(s)
	{
		if (!s)
			return '';
		let text = (s.status || '?').replace(/_/g, ' ') + ' (' + (s.confidence || '?') + ' confidence); suggested: ' + (s.action || '?').replace(/_/g, ' ');
		if (s.rejectReason)
			text += ' (' + label(s.rejectReason) + ')';
		if (s.suggestedSeverity)
			text += '; severity ' + s.suggestedSeverity;
		if (s.checkedSha)
			text += '; checked ' + shortSha(s.checkedSha);
		return text;
	}

	export default {
		props:
		{
			findingId: {
				type: Number,
				required: true
			}
		},
		components: { SlideButton },
		data()
		{
			return {
				severities,
				rejectReasons,
				slideColors,
				slideCounter: 0,
				data: null,
				loading: false,
				error: null,
				loadToken: 0,
				busy: false,
				actionError: null,
				actionInfo: null,
				editing: false,
				editTitle: '',
				editDescription: '',
				editVersion: 0,
				showTextVersions: false,
				severityChoice: '',
				severityNote: '',
				newComment: '',
				editingCommentId: null,
				editingCommentText: '',
				openCommentVersions: {},
				rejectOpen: false,
				rejectReason: null,
				rejectNote: '',
				promptOpen: false,
				prompt: { markdown: '', fileName: '', headError: null, headBranch: null },
				copyStatus: '',
				declineNote: '',
				stateTimer: null,
				raw: {},
				infoBatchId: null
			};
		},
		created()
		{
			this.load();
		},
		beforeUnmount()
		{
			clearTimeout(this.stateTimer);
		},
		watch:
		{
			findingId()
			{
				clearTimeout(this.stateTimer);
				this.raw = {};
				this.declineNote = '';
				this.data = null;
				this.editing = false;
				this.editingCommentId = null;
				this.showTextVersions = false;
				this.openCommentVersions = {};
				this.actionError = null;
				this.actionInfo = null;
				this.load();
			}
		},
		computed:
		{
			f()
			{
				return this.data ? this.data.finding : {};
			},
			t()
			{
				return this.data ? this.data.target : null;
			},
			state()
			{
				return this.data && this.data.reEvaluationState ? this.data.reEvaluationState : { reviewsEnabled: true };
			},
			re()
			{
				return this.state.reEvaluation || null;
			},
			suggestion()
			{
				return this.state.pendingSuggestion || null;
			},
			showFixRow()
			{
				if (!this.data)
					return false;
				let fixStatus = ['ApprovedBatch', 'ApprovedImmediate', 'FixInProgress'].indexOf(this.f.status) !== -1;
				return fixStatus || !!this.data.fixBatch || (this.data.fixesEnabled && this.f.status === 'Open');
			}
		},
		methods:
		{
			label,
			severityClass,
			statusClass,
			formatTime,
			formatCost,
			location,
			shortSha,
			findingRoute(id)
			{
				return { name: 'codeReviewFinding', params: { id: id } };
			},
			async load()
			{
				if (isNaN(this.findingId))
				{
					this.error = 'Invalid finding ID.';
					return;
				}
				let token = ++this.loadToken;
				this.loading = true;
				this.error = null;
				try
				{
					let data = await GetFinding(this.findingId);
					if (token !== this.loadToken)
						return;
					if (data.success)
					{
						let previousRunId = this.re && this.re.run ? this.re.run.id : null;
						this.data = data;
						this.severityChoice = data.finding.effectiveSeverity;
						document.title = data.finding.displayId + ' ' + data.finding.title + ' - ' + appContext.systemName;
						if (!this.re || !this.re.run || this.re.run.id !== previousRunId)
							this.raw = {};
						this.scheduleStatePoll();
					}
					else
						this.error = data.error;
				}
				catch (ex)
				{
					if (token === this.loadToken)
						this.error = ex.message;
				}
				finally
				{
					if (token === this.loadToken)
						this.loading = false;
				}
			},
			/**
			 * Runs a write request, shows its error, and reloads the finding after a change.  Returns true on success.
			 */
			async runAction(fn, unchangedMessage)
			{
				if (this.busy)
					return false;
				this.busy = true;
				this.actionError = null;
				this.actionInfo = null;
				this.infoBatchId = null;
				try
				{
					let response = await fn();
					if (!response.success)
					{
						this.actionError = response.error;
						return false;
					}
					if (response.changed === false && unchangedMessage)
						this.actionInfo = unchangedMessage;
					await this.load();
					return true;
				}
				catch (ex)
				{
					this.actionError = ex.message;
					return false;
				}
				finally
				{
					this.busy = false;
					this.slideCounter++;
				}
			},
			startEdit()
			{
				this.editTitle = this.f.title || '';
				this.editDescription = this.f.description || '';
				this.editVersion = this.f.version;
				this.editing = true;
				this.$nextTick(() => { if (this.$refs.editTitle) this.$refs.editTitle.focus(); });
			},
			cancelEdit()
			{
				this.editing = false;
				this.actionError = null;
			},
			async saveEdit()
			{
				let ok = await this.runAction(() => EditFinding(this.f.id, this.editTitle, this.editDescription, this.editVersion), 'Nothing changed.');
				if (ok)
					this.editing = false;
			},
			async changeSeverity()
			{
				let ok = await this.runAction(() => SetSeverity(this.f.id, this.severityChoice, this.severityNote), 'The severity was already ' + this.severityChoice + '.');
				if (ok)
					this.severityNote = '';
			},
			async markFixed()
			{
				await this.runAction(() => SetStatus(this.f.id, 'markFixed', null, null), 'Nothing changed.');
			},
			async reopen()
			{
				await this.runAction(() => SetStatus(this.f.id, 'reopen', null, null), 'Nothing changed.');
			},
			openReject()
			{
				this.rejectReason = null;
				this.rejectNote = '';
				this.actionError = null;
				this.rejectOpen = true;
				this.$nextTick(() =>
				{
					let b = this.$refs.firstReason;
					if (Array.isArray(b))
						b = b[0];
					if (b)
						b.focus();
				});
			},
			closeReject()
			{
				if (!this.busy)
					this.rejectOpen = false;
			},
			async confirmReject()
			{
				let ok = await this.runAction(() => SetStatus(this.f.id, 'reject', this.rejectReason, this.rejectNote), 'Nothing changed.');
				if (ok)
					this.rejectOpen = false;
			},
			startCommentEdit(c)
			{
				this.editingCommentId = c.id;
				this.editingCommentText = c.text;
			},
			async saveCommentEdit(c)
			{
				let ok = await this.runAction(() => EditComment(c.id, this.editingCommentText), 'Nothing changed.');
				if (ok)
					this.editingCommentId = null;
			},
			toggleCommentVersions(id)
			{
				this.openCommentVersions = Object.assign({}, this.openCommentVersions, { [id]: !this.openCommentVersions[id] });
			},
			async addComment()
			{
				let ok = await this.runAction(() => AddComment(this.f.id, this.newComment));
				if (ok)
					this.newComment = '';
			},
			async generatePrompt()
			{
				if (this.busy)
					return;
				this.busy = true;
				this.actionError = null;
				this.actionInfo = null;
				try
				{
					let response = await GeneratePrompt(this.f.id);
					if (!response.success)
					{
						this.actionError = response.error;
						return;
					}
					this.prompt = response;
					this.copyStatus = '';
					this.promptOpen = true;
					this.$nextTick(() => { if (this.$refs.copyButton) this.$refs.copyButton.focus(); });
					this.load();
				}
				catch (ex)
				{
					this.actionError = ex.message;
				}
				finally
				{
					this.busy = false;
				}
			},
			/**
			 * The finding as plain text for an AI chat, framed by blank lines and BEGIN/END marker lines so it stands apart from the
			 * user's own words around it.
			 */
			buildAiExport()
			{
				let f = this.f;
				let t = this.t;
				let lines = [];
				let section = (name, text) =>
				{
					if (text && String(text).trim())
						lines.push('', '--- ' + name + ' ---', String(text).trim());
				};
				lines.push('', '===== BEGIN CODE REVIEW FINDING ' + f.displayId + ' (TDS App Service) =====');
				lines.push(f.displayId + ': ' + f.title);
				lines.push('Link: ' + window.location.origin + '/codereview/finding/' + f.id);
				lines.push('Repository: ' + f.repo);
				let where = location(f);
				if (where)
					lines.push('Location: ' + where + (f.fileUrl ? ' (at the reviewed commit: ' + f.fileUrl + ')' : ''));
				let status = 'Status: ' + label(f.status);
				if (f.rejectReason && f.rejectReason !== 'None')
					status += ' (' + label(f.rejectReason) + (f.rejectNote && !f.rejectCommentId ? ': ' + f.rejectNote : '') + ')';
				lines.push(status);
				lines.push('Severity: ' + f.effectiveSeverity + ' (reviewer claimed ' + f.claimedSeverity + ', triage ' + f.finalSeverity + ')');
				lines.push('Triage verdict: ' + (f.verdict === 'None' ? 'not triaged' : f.verdict) + '; category: ' + label(f.category) + '; introduced by the push: ' + f.introducedByPush + '; fix complexity: ' + label(f.fixComplexity));
				if (f.duplicateOfId)
					lines.push('Duplicate of CR-' + f.duplicateOfId);
				if (t)
				{
					lines.push('Reviewed push: ' + t.repo + ' push ' + t.pushId + ' (' + shortSha(t.oldSha) + '..' + shortSha(t.newSha) + ') by ' + t.pusher + ', ' + formatTime(t.pushedAt));
					for (let c of t.commits || [])
						lines.push('- ' + shortSha(c.sha) + ' ' + c.subject + (c.author ? ' (' + c.author + ')' : ''));
				}
				section('Description', f.description);
				if (f.originalSummary || f.originalScenario)
					section('Original reviewer report', (f.originalSummary ? 'Summary: ' + f.originalSummary : '') + (f.originalScenario ? '\nFailure scenario: ' + f.originalScenario : ''));
				section('Triage rationale', f.triageRationale);
				if (f.demotionEvidence && f.demotionEvidence.length)
					section('Demotion evidence', f.demotionEvidence.map(e => '- ' + e.file + (e.line ? ':' + e.line : '') + ': ' + e.explanation).join('\n'));
				if (f.intentEvidence && f.intentEvidence.length)
					section('Recorded as intended', f.intentEvidence.map(e => '- ' + e.source + ': ' + e.explanation).join('\n'));
				let comments = this.data.comments || [];
				if (comments.length)
					section('Comments (' + comments.length + ', oldest first)', comments.map(c => '[' + c.author + ', ' + formatTime(c.createdAt) + (c.editedAt ? ', edited' : '') + ']\n' + c.text).join('\n\n'));
				lines.push('', '===== END CODE REVIEW FINDING ' + f.displayId + ' =====', '');
				return lines.join('\n');
			},
			async copyForAi()
			{
				this.actionError = null;
				this.actionInfo = null;
				let text = this.buildAiExport();
				let ok = false;
				try
				{
					await navigator.clipboard.writeText(text);
					ok = true;
				}
				catch (ex)
				{
					// Clipboard API unavailable (e.g. plain http): copy through a temporary text area.
					let ta = document.createElement('textarea');
					ta.value = text;
					ta.setAttribute('readonly', '');
					ta.style.position = 'fixed';
					ta.style.opacity = '0';
					document.body.appendChild(ta);
					ta.select();
					try
					{
						ok = document.execCommand('copy');
					}
					catch (ex2)
					{
					}
					document.body.removeChild(ta);
				}
				if (ok)
					this.actionInfo = 'Copied ' + this.f.displayId + ' (' + text.length + ' characters); paste it into the AI chat.';
				else
					this.actionError = 'Copying failed; the browser did not allow access to the clipboard.';
			},
			async copyPrompt()
			{
				try
				{
					await navigator.clipboard.writeText(this.prompt.markdown);
					this.copyStatus = 'Copied.';
				}
				catch (ex)
				{
					// Clipboard API unavailable (e.g. plain http): select the text and fall back to execCommand.
					let ta = this.$refs.promptText;
					if (ta)
					{
						ta.focus();
						ta.select();
						let ok = false;
						try
						{
							ok = document.execCommand('copy');
						}
						catch (ex2)
						{
						}
						this.copyStatus = ok ? 'Copied.' : 'Copy failed; the text is selected, press Ctrl+C.';
					}
				}
			},
			downloadPrompt()
			{
				let blob = new Blob([this.prompt.markdown], { type: 'text/markdown;charset=utf-8' });
				let url = URL.createObjectURL(blob);
				let a = document.createElement('a');
				a.href = url;
				a.download = this.prompt.fileName || 'prompt.md';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				setTimeout(() => URL.revokeObjectURL(url), 1000);
			},
			triggerLabel(trigger)
			{
				switch (trigger)
				{
					case 'Manual': return 'Manual';
					case 'FixPushed': return 'After a fix push';
					case 'FileTouched': return 'A push changed the file';
					default: return trigger || '';
				}
			},
			reStatusLabel(re)
			{
				switch (re.status)
				{
					case 'Queued': return re.retryAfter ? 'Queued (retry at ' + formatTime(re.retryAfter) + ')' : 'Queued';
					case 'Safety': return 'Blocked by safety systems';
					default: return label(re.status);
				}
			},
			/**
			 * While the latest re-evaluation is queued or running, checks it every few seconds and reloads the page when it ends.
			 */
			scheduleStatePoll()
			{
				clearTimeout(this.stateTimer);
				this.stateTimer = null;
				if (this.re && this.re.isActive)
					this.stateTimer = setTimeout(() => this.pollState(), reEvaluationPollMs);
			},
			async pollState()
			{
				let id = this.findingId;
				try
				{
					let s = await GetReEvaluationState(id);
					if (id !== this.findingId || !this.data)
						return;
					if (s.success)
					{
						if (!s.reEvaluation || !s.reEvaluation.isActive)
						{
							// Finished: reload everything (comment, history, status).
							await this.load();
							return;
						}
						this.data.reEvaluationState = s;
					}
				}
				catch (ex)
				{
					// Try again on the next tick.
				}
				if (id === this.findingId)
					this.scheduleStatePoll();
			},
			async approveFix(immediate)
			{
				let batchId = null;
				let ok = await this.runAction(async () =>
				{
					let response = await ApproveFix(this.f.id, immediate, null);
					batchId = response.success ? response.fixBatchId : null;
					return response;
				});
				if (ok)
				{
					this.actionInfo = immediate ? (batchId ? 'Approved; fix batch FB-' + batchId + ' starts as soon as the agent is free.' : 'Approved for an immediate fix.') : 'Approved for the next batched fix.';
					this.infoBatchId = batchId;
				}
			},
			async unapproveFix()
			{
				let ok = await this.runAction(() => UnapproveFix(this.f.id, null), 'The finding was not approved for a fix.');
				if (ok && !this.actionInfo)
					this.actionInfo = 'Fix approval taken back.';
			},
			async reEvaluate()
			{
				await this.runAction(() => ReEvaluate(this.f.id), 'A re-evaluation of this commit is already running.');
			},
			async acceptSuggestion()
			{
				let s = this.suggestion;
				if (!s || this.busy)
					return;
				let openPrompt = false;
				let acceptedBatchId = null;
				let ok = await this.runAction(async () =>
				{
					let response = await AcceptSuggestion(this.f.id, s.runId);
					openPrompt = !!(response.success && response.openPrompt);
					acceptedBatchId = response.success ? response.fixBatchId || null : null;
					return response;
				});
				if (ok)
				{
					this.actionInfo = 'Suggestion accepted.';
					this.infoBatchId = acceptedBatchId;
					if (openPrompt)
						this.generatePrompt();
				}
			},
			async declineSuggestion()
			{
				let s = this.suggestion;
				if (!s || this.busy)
					return;
				let ok = await this.runAction(() => DeclineSuggestion(this.f.id, s.runId, this.declineNote));
				if (ok)
				{
					this.declineNote = '';
					this.actionInfo = 'Suggestion declined.';
				}
			},
			showComment(commentId)
			{
				let el = document.getElementById('crfC' + commentId);
				if (el)
				{
					el.scrollIntoView({ behavior: 'smooth', block: 'center' });
					el.focus({ preventScroll: true });
				}
			},
			async rawToggled(e)
			{
				let run = this.re && this.re.run;
				if (!e.target.open || !run || this.raw.loading || (this.raw.data && this.raw.runId === run.id))
					return;
				this.raw = { loading: true, runId: run.id };
				let entry;
				try
				{
					let data = await GetRunOutput(run.id);
					entry = data.success ? { data: data, runId: run.id } : { error: data.error, runId: run.id };
				}
				catch (ex)
				{
					entry = { error: ex.message, runId: run.id };
				}
				this.raw = entry;
			},
			prettyJson(s)
			{
				if (!s)
					return '(empty)';
				try
				{
					return JSON.stringify(JSON.parse(s), null, 2);
				}
				catch (ex)
				{
					return s;
				}
			},
			eventDetails(e)
			{
				switch (e.type)
				{
					case 'ActionSuggested':
						return describeSuggestion(parseSuggestion(e.after)) + (e.runId ? ' (run #' + e.runId + ')' : '');
					case 'ActionAccepted':
					case 'ActionDeclined':
					{
						let s = parseSuggestion(e.after);
						return (e.type === 'ActionAccepted' ? 'Accepted' : 'Declined') + ' the suggestion of run #' + (e.runId || '?') + (s ? ' (' + (s.action || '?').replace(/_/g, ' ') + ')' : '') + (e.note ? '\n' + e.note : '');
					}
					case 'ReEvaluationRequested':
					case 'ReEvaluationFailed':
					case 'ReEvaluationCanceled':
						return (e.note || '') + (e.runId && e.type !== 'ReEvaluationRequested' ? ' (run #' + e.runId + ')' : '');
					case 'Created':
						return 'Created at ' + (e.after || '?') + ' severity' + (e.runId ? ' (run #' + e.runId + ')' : '');
					case 'Edited':
						return 'Title/description edited' + (e.note ? ', now ' + e.note : '');
					case 'CommentAdded':
						return 'Comment ' + (e.commentId || '');
					case 'CommentEdited':
						return 'Comment ' + (e.commentId || '') + (e.note ? ', now ' + e.note : '');
					case 'StatusChanged':
					case 'SeverityChanged':
						return statusText(e.before) + ' → ' + statusText(e.after) + (e.note ? '\n' + e.note : '');
					case 'RepeatSeen':
						return 'Raised again for ' + (e.note || '?') + (e.runId ? ' (run #' + e.runId + ')' : '');
					case 'CommitLinked':
						return 'Commit ' + (e.after || '');
					case 'Notified':
						return 'Email sent';
					default:
					{
						let parts = [];
						if (e.before || e.after)
							parts.push((e.before || '') + ' → ' + (e.after || ''));
						if (e.note)
							parts.push(e.note);
						return parts.join('\n');
					}
				}
			}
		}
	};
</script>

<style scoped src="./codeReview.css"></style>
<style scoped>
	.fixRow
	{
		border-top: 1px solid #E2E2E2;
		padding-top: 4px;
	}

	.fixLabel
	{
		font-weight: bold;
	}

	.headRow .pageHeading
	{
		font-size: 22px;
		word-break: break-word;
	}

	.sectionHead
	{
		margin-bottom: 4px;
	}

		.sectionHead .secondaryHeading
		{
			margin: 0;
		}

	.subHeading
	{
		font-size: 14px;
		font-weight: bold;
		margin: 0.8em 0 0.2em 0;
	}

	.twoCol
	{
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
		gap: 0 10px;
	}

	.editGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 6px 10px;
		align-items: start;
	}

		.editGrid > label
		{
			font-weight: bold;
			padding-top: 3px;
		}

	textarea
	{
		font-family: Consolas, monospace;
		font-size: 13px;
		resize: vertical;
	}

	.wide
	{
		width: 100%;
		box-sizing: border-box;
	}

	.versions
	{
		border-left: 3px solid #CCCCCC;
		margin-top: 8px;
		padding-left: 10px;
	}

	.version
	{
		margin-bottom: 8px;
		color: #333333;
	}

	.compactList
	{
		margin: 4px 0;
		padding-left: 20px;
	}

	.severityForm
	{
		margin-top: 8px;
	}

	.comment
	{
		border-bottom: 1px solid #EEEEEE;
		padding: 6px 0;
	}

		.comment:focus
		{
			outline: 2px solid #123A70;
			outline-offset: 2px;
		}

	.suggestion
	{
		border-left: 4px solid #123A70;
		background-color: #F3F7FC;
		padding: 4px 10px 6px 10px;
		margin: 6px 0 8px 0;
	}

		.suggestion .subHeading
		{
			margin-top: 2px;
		}

	.suggestionButtons input[type=text]
	{
		min-width: 18em;
	}

	.runCost
	{
		margin-left: 6px;
	}

	.rawOutput
	{
		margin-top: 6px;
	}

		.rawOutput summary
		{
			cursor: pointer;
			color: #0B57A6;
		}

	.scrollPre
	{
		max-height: 400px;
		overflow: auto;
		background-color: #F7F7F7;
		border: 1px solid #DDDDDD;
		padding: 6px;
		font-size: 12px;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.commentHead
	{
		margin: 0 0 2px 0;
	}

	.addComment
	{
		margin-top: 8px;
	}

		.addComment > label
		{
			font-weight: bold;
			display: block;
			margin-bottom: 2px;
		}

	.history td
	{
		font-size: 12px;
	}

	.visuallyHidden
	{
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border: 0;
	}

	.wideDialog
	{
		width: 900px;
	}

	.promptText
	{
		min-height: 300px;
		background-color: #F7F7F7;
	}
</style>
